// src/components/ReadjudicacionMasiva/Auditoria/ValidacionPanel.js

import React from 'react';
import makeStyles from '@mui/styles/makeStyles';
import {
    Paper,
    Typography,
    Grid,
    Box,
    LinearProgress,
    Chip
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Warning as WarningIcon,
    RemoveCircle as RemoveCircleIcon
} from '@mui/icons-material';

const useStyles = makeStyles((theme) => ({
    paper: {
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2)
    },
    title: {
        fontWeight: 600,
        marginBottom: theme.spacing(2)
    },
    statBox: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(1.5),
        padding: theme.spacing(2),
        borderRadius: theme.shape.borderRadius,
        border: `1px solid ${theme.palette.divider}`,
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: theme.shadows[4]
        }
    },
    statBoxActive: {
        boxShadow: theme.shadows[6],
        borderWidth: 2
    },
    statIcon: {
        fontSize: 40
    },
    statValue: {
        fontSize: '1.75rem',
        fontWeight: 700,
        lineHeight: 1
    },
    statLabel: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.25)
    },
    correctos: {
        color: theme.palette.success.main,
        backgroundColor: theme.palette.success.light + '20'
    },
    defectuosos: {
        color: theme.palette.error.main,
        backgroundColor: theme.palette.error.light + '20'
    },
    pendientes: {
        color: theme.palette.warning.main,
        backgroundColor: theme.palette.warning.light + '20'
    },
    excluidos: {
        color: theme.palette.grey[600],
        backgroundColor: theme.palette.grey[200]
    },
    progressBox: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(2)
    },
    progressBar: {
        height: 10,
        borderRadius: 5,
        marginTop: theme.spacing(1)
    },
    chip: {
        marginTop: theme.spacing(2)
    }
}));

const ValidacionPanel = ({ registros, filtroActivo, onFiltroChange }) => {
    const classes = useStyles();

    const calcularEstadisticas = () => {
        const total = registros.length;
        const correctos = registros.filter(r => r.Ok === 1).length;
        const defectuosos = registros.filter(r => r.Ok === 0).length;
        const pendientes = registros.filter(r => r.Ok === 2).length;
        const excluidos = registros.filter(r => r.Ok === 3).length;

        const progreso = total > 0 ? (correctos / total) * 100 : 0;
        const porcentajeCorrectos = total > 0 ? ((correctos / total) * 100).toFixed(1) : 0;

        return {
            total,
            correctos,
            defectuosos,
            pendientes,
            excluidos,
            progreso,
            porcentajeCorrectos
        };
    };

    const stats = calcularEstadisticas();

    const getEstadoChip = () => {
        if (stats.defectuosos > 0) {
            return { label: 'Con Errores', color: 'error' };
        }
        if (stats.pendientes > 0) {
            return { label: 'Pendiente', color: 'warning' };
        }
        if (stats.correctos === stats.total && stats.total > 0) {
            return { label: 'Completo', color: 'primary' };
        }
        return { label: 'Sin Auditar', color: 'warning' };
    };

    const estadoChip = getEstadoChip();

    const handleFiltroClick = (tipo) => {
        if (onFiltroChange) {
            // Si ya está activo el mismo filtro, lo desactiva (muestra todos)
            onFiltroChange(filtroActivo === tipo ? null : tipo);
        }
    };

    return (
        <Paper className={classes.paper}>
            <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={2}>
                <Typography variant="h6" className={classes.title} style={{ marginBottom: 0 }}>
                    Resumen de Validación
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" color="text.secondary">
                        Progreso: {stats.porcentajeCorrectos}%
                    </Typography>
                    <Chip
                        label={estadoChip.label}
                        color={estadoChip.color}
                        size="small"
                    />
                </Box>
            </Box>

            <LinearProgress
                variant="determinate"
                value={stats.progreso}
                className={classes.progressBar}
                color="primary"
                style={{ marginBottom: 16 }}
            />

            <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Box
                        className={`${classes.statBox} ${classes.correctos} ${filtroActivo === 1 ? classes.statBoxActive : ''}`}
                        onClick={() => handleFiltroClick(1)}
                    >
                        <CheckCircleIcon className={classes.statIcon} />
                        <Box>
                            <Typography className={classes.statValue}>
                                {stats.correctos}
                            </Typography>
                            <Typography className={classes.statLabel}>
                                Correctos
                            </Typography>
                        </Box>
                    </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Box
                        className={`${classes.statBox} ${classes.defectuosos} ${filtroActivo === 0 ? classes.statBoxActive : ''}`}
                        onClick={() => handleFiltroClick(0)}
                    >
                        <ErrorIcon className={classes.statIcon} />
                        <Box>
                            <Typography className={classes.statValue}>
                                {stats.defectuosos}
                            </Typography>
                            <Typography className={classes.statLabel}>
                                Defectuosos
                            </Typography>
                        </Box>
                    </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Box
                        className={`${classes.statBox} ${classes.pendientes} ${filtroActivo === 2 ? classes.statBoxActive : ''}`}
                        onClick={() => handleFiltroClick(2)}
                    >
                        <WarningIcon className={classes.statIcon} />
                        <Box>
                            <Typography className={classes.statValue}>
                                {stats.pendientes}
                            </Typography>
                            <Typography className={classes.statLabel}>
                                Pendientes
                            </Typography>
                        </Box>
                    </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Box
                        className={`${classes.statBox} ${classes.excluidos} ${filtroActivo === 3 ? classes.statBoxActive : ''}`}
                        onClick={() => handleFiltroClick(3)}
                    >
                        <RemoveCircleIcon className={classes.statIcon} />
                        <Box>
                            <Typography className={classes.statValue}>
                                {stats.excluidos}
                            </Typography>
                            <Typography className={classes.statLabel}>
                                Excluidos
                            </Typography>
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </Paper>
    );
};

export default ValidacionPanel;
